import React, { useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useState } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { scenes, getSceneById, buildTimelineFromSteps, buildTimelineFromKeyframes } from '../utils/scenes.ts'; // Adjust path if needed

// Wrap with forwardRef to accept a ref from App.jsx
const ScenePlayer = forwardRef(({
    // activeSceneId, // No longer directly triggers useEffect
    orbitControlsRef, 
    meshGroups,
    timelineRef, 
    onTimelineChange,
    onReady, // <<< Add onReady prop
}, ref) => {
    console.log("ScenePlayer: Rendering/Re-rendering...");
    console.log(`ScenePlayer: Received props - typeof onReady=${typeof onReady}`); // <<< Log received prop type
    const { camera } = useThree(); 
    const [isAnimating, setIsAnimating] = useState(false);
    const [currentScene, setCurrentScene] = useState(null);
    const activeSceneTimelineRef = useRef(null); // <<< Add ref for current scene timeline
    const animationRef = useRef(null);

    // --- Log Mount ---
    useEffect(() => {
      console.log("ScenePlayer: Component Mounted.");
      return () => {
          console.log("ScenePlayer: Component Unmounted.");
          if (animationRef.current) {
              cancelAnimationFrame(animationRef.current);
          }
          if (activeSceneTimelineRef.current) {
              activeSceneTimelineRef.current.pause();
          }
      };
    }, []); // Empty dependency array ensures this runs only on mount/unmount

    // --- Core Animation Logic (Moved from useEffect) ---
    const playSceneAnimation = useCallback((sceneConfig) => {
        if (!sceneConfig || !orbitControlsRef.current) {
            console.log(`ScenePlayer: Skipping playSceneAnimation (inactive config or no controls).`);
            return;
        }

        // --- Stop and clear previous scene timeline if it exists --- 
        if (activeSceneTimelineRef.current) {
            console.log("ScenePlayer: Pausing and removing previous scene timeline.");
            activeSceneTimelineRef.current.pause();
            activeSceneTimelineRef.current = null;
        }
        // -----------------------------------------------------------
        
        // <<< Define controls EARLIER >>>
        const controls = orbitControlsRef.current;
        if (!controls) { // Add null check for controls
             console.error("ScenePlayer: OrbitControls ref is not current!");
             return; // Cannot proceed without controls
        }

        // Stop individual camera/target animations just in case
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }

        console.log(`ScenePlayer: Playing scene: ${sceneConfig.label}`);
        // const controls = orbitControlsRef.current; // <<< REMOVE duplicate definition
        onTimelineChange?.(null); // Clear visualizer initially

        // --- Check for Keyframes --- 
        console.log(`ScenePlayer: Checking config for ${sceneConfig.label}:`, sceneConfig);
        console.log(`ScenePlayer: keyframeAnimations property:`, sceneConfig.keyframeAnimations);
        console.log(`ScenePlayer: Object.keys(keyframeAnimations || {}).length:`, Object.keys(sceneConfig.keyframeAnimations || {}).length);

        if (Array.isArray(sceneConfig.keyframeAnimations) && sceneConfig.keyframeAnimations.length > 0) {
            console.log(`ScenePlayer: Found keyframeAnimations array for ${sceneConfig.label}`);
            try {
                // Set initial state (important for replay)
                controls.target.set(...sceneConfig.cameraState.target);
                camera.position.set(...sceneConfig.cameraState.position);
                controls.update();
                
                const handleSequenceComplete = () => {
                    console.log(`ScenePlayer: Received onComplete for ${sceneConfig.label} keyframes.`);
                    activeSceneTimelineRef.current = null; // <<< Clear the ref on complete
                    onTimelineChange?.(null);
                };

                const builtTimeline = buildTimelineFromKeyframes(
                    sceneConfig.keyframeAnimations, 
                    camera, 
                    controls, 
                    meshGroups, 
                    handleSequenceComplete
                );

                if (builtTimeline) {
                    activeSceneTimelineRef.current = builtTimeline; // <<< Store the timeline
                    onTimelineChange?.(builtTimeline);
                    builtTimeline.play();
                    console.log("ScenePlayer: Started timeline built from keyframes.");
                } else {
                    console.error(`ScenePlayer: Failed to build timeline from keyframes for ${sceneConfig.label}.`);
                    onTimelineChange?.(null);
                }

            } catch (error) {
                console.error(`ScenePlayer: Error building/running timeline from keyframes for ${sceneConfig.label}:`, error);
                onTimelineChange?.(null);
            }
        } else if (sceneConfig.animationSteps && sceneConfig.animationSteps.length > 0) {
            // --- Fallback to Animation Steps --- 
            console.log(`ScenePlayer: Found animationSteps for ${sceneConfig.label} (using steps)`);
            try {
                // Set initial state based on scene config before building timeline
                controls.target.set(...sceneConfig.cameraState.target);
                camera.position.set(...sceneConfig.cameraState.position);
                controls.update();
                
                const handleSequenceComplete = () => {
                    console.log(`ScenePlayer: Received onComplete for ${sceneConfig.label} steps.`);
                    activeSceneTimelineRef.current = null; // <<< Clear the ref on complete
                    onTimelineChange?.(null);
                };

                // Build the timeline using the utility function
                const builtTimeline = buildTimelineFromSteps(
                    sceneConfig.animationSteps, 
                    camera, 
                    controls, 
                    meshGroups, 
                    handleSequenceComplete
                );

                if (builtTimeline) {
                    activeSceneTimelineRef.current = builtTimeline; // <<< Store the timeline
                    onTimelineChange?.(builtTimeline); // Pass timeline up for visualizer
                    builtTimeline.play(); // <<< IMPORTANT: Play the timeline
                    console.log("ScenePlayer: Started timeline built from steps.");
                } else {
                    console.error(`ScenePlayer: Failed to build timeline from steps for ${sceneConfig.label}.`);
                    onTimelineChange?.(null);
                }

            } catch (error) {
                console.error(`ScenePlayer: Error building/running timeline from steps for ${sceneConfig.label}:`, error);
                onTimelineChange?.(null);
            }
        } else {
             // --- Fallback to Simple Camera Transition ---
            console.log(`ScenePlayer: No keyframes or steps found, running simple transition for ${sceneConfig.label}`);
            
            // <<< ADD CHECKS for cameraState existence >>>
            if (!sceneConfig.cameraState?.position || !sceneConfig.cameraState?.target) {
                console.error(`ScenePlayer: Scene '${sceneConfig.label}' is missing cameraState or its properties for simple transition. Aborting transition.`);
                onTimelineChange?.(null); // Ensure visualizer is cleared
                return; // Stop execution for this scene
            }
            // <<< END CHECKS >>>

            const targetPosition = new THREE.Vector3(...sceneConfig.cameraState.position);
            const targetLookAt = new THREE.Vector3(...sceneConfig.cameraState.target);
            const startPosition = camera.position.clone();
            const startLookAt = controls.target.clone();
            const duration = sceneConfig.duration || 1000;
            const startTime = Date.now();

            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                // Use cubic easing
                const easedProgress = progress < 0.5
                    ? 4 * progress * progress * progress
                    : 1 - Math.pow(-2 * progress + 2, 3) / 2;

                // Interpolate position and target
                camera.position.lerpVectors(startPosition, targetPosition, easedProgress);
                controls.target.lerpVectors(startLookAt, targetLookAt, easedProgress);
                controls.update();

                if (progress < 1) {
                    animationRef.current = requestAnimationFrame(animate);
                } else {
                    console.log(`ScenePlayer: Simple transition complete for ${sceneConfig.label}`);
                    // Run Entry FX after simple transition
                    if (sceneConfig.entryFX && timelineRef.current) {
                        console.log(`ScenePlayer: Running entryFX for ${sceneConfig.label}`);
                        try {
                            sceneConfig.entryFX(meshGroups, timelineRef.current);
                        } catch (error) {
                            console.error(`ScenePlayer: Error executing entryFX for ${sceneConfig.label}:`, error);
                        }
                    } else {
                        console.log(`ScenePlayer: No entryFX defined for ${sceneConfig.label}`);
                    }
                    onTimelineChange?.(null);
                }
            };

            animationRef.current = requestAnimationFrame(animate);
        }
    }, [camera, orbitControlsRef, meshGroups, timelineRef, onTimelineChange]); // Removed isAnimating, timelineRef

    // --- NEW Preview Animation Logic ---
    const previewAnimation = useCallback((keyframesArray) => {
        if (!orbitControlsRef.current || !camera) {
            console.warn("ScenePlayer: Cannot run preview, controls or camera not ready.");
            return;
        }
        if (!keyframesArray || keyframesArray.length < 2) {
            console.warn("ScenePlayer: Need at least 2 keyframes for preview.");
            return; // Already checked in App, but good practice
        }

        console.log("ScenePlayer: Starting preview animation...");
        const controls = orbitControlsRef.current;

        // Stop any existing camera/target animations & timelines
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }
        // Consider pausing other running timelines if necessary
        // anime.running.forEach(anim => anim.pause());

        // Set initial state to the first keyframe INSTANTLY
        try {
            const firstKf = keyframesArray[0];
            camera.position.set(firstKf.camera.position.x, firstKf.camera.position.y, firstKf.camera.position.z);
            controls.target.set(firstKf.controls.target.x, firstKf.controls.target.y, firstKf.controls.target.z);
            controls.update(); // Ensure controls internal state matches target
        } catch (e) {
            console.error("ScenePlayer Preview: Error setting initial keyframe state:", e, keyframesArray[0]);
            return;
        }

        // Create a new timeline for the preview
        const previewTimeline = anime.timeline({
            autoplay: false,
            easing: 'easeInOutSine', // <<< Use a smoother easing
            complete: () => {
                console.log("ScenePlayer: Preview animation complete.");
                // Maybe reset isAnimating.current if we add a lock for preview?
            }
        });

        // Add tweens for each segment
        const segmentDuration = 1000; // Duration for each keyframe transition (ms)
        for (let i = 1; i < keyframesArray.length; i++) {
            const prevKf = keyframesArray[i - 1];
            const currKf = keyframesArray[i];

            // Validate keyframes before adding
            if (!currKf?.camera?.position || !currKf?.controls?.target) {
                console.error(`ScenePlayer Preview: Invalid keyframe structure at index ${i}`, currKf);
                continue; 
            }

            const offset = (i - 1) * segmentDuration; // Define offset for clarity

            previewTimeline.add({
                targets: camera.position,
                x: currKf.camera.position.x,
                y: currKf.camera.position.y,
                z: currKf.camera.position.z,
                duration: segmentDuration,
            }, offset); // <<< Add position tween at calculated offset

            previewTimeline.add({
                targets: controls.target,
                x: currKf.controls.target.x,
                y: currKf.controls.target.y,
                z: currKf.controls.target.z,
                duration: segmentDuration,
                update: () => controls.update(), // IMPORTANT: Update controls during target tween
            }, offset); // <<< Add target tween at calculated offset
        }
        
        console.log("ScenePlayer: Playing preview timeline.");
        previewTimeline.play();
        onTimelineChange?.(previewTimeline); // <<< Pass preview timeline up

    }, [camera, orbitControlsRef, onTimelineChange]); // <<< Add onTimelineChange dependency

    // --- Expose the play function AND preview function via the ref --- 
    useImperativeHandle(ref, () => {
        console.log("ScenePlayer: useImperativeHandle executing. Received ref:", ref);
        const handle = { 
          playSceneAnimation,
          previewAnimation,
          stopAll: () => {
            console.log("ScenePlayer: stopAll called.");
            // Stop individual animations
            if(camera) cancelAnimationFrame(animationRef.current);
            if(orbitControlsRef.current) cancelAnimationFrame(animationRef.current);
            // Stop active scene timeline
            if (activeSceneTimelineRef.current) {
                activeSceneTimelineRef.current.pause();
                activeSceneTimelineRef.current = null;
            }
            // Potentially stop other specific animations if needed later
            onTimelineChange?.(null); // Clear visualizer
          }
        };
        onReady?.(); 
        return handle; 
    }, [playSceneAnimation, previewAnimation, onReady]); // <<< Add onReady dependency

    // No UI rendered by ScenePlayer itself
    return null;
});

export default ScenePlayer; 